import { Route, Routes } from '@angular/router';
import { AppInitializationGuard, PaceSpectorComponent, WidgetsComponent } from '@evorait/pace-core';
import { DocboxComponent, PaceForms2Guard } from '@evorait/pace-forms';
import { ConfirmationCanActivateGuard } from './confirmation/confirmation.can-activate.guard';
import { ConfirmationCanDeactivateGuard } from './confirmation/confirmation.can-deactivate.guard';
import { ConfirmationComponent } from './confirmation/confirmation/confirmation.component';
import { ConfirmationResolver } from './confirmation/confirmation/confirmation.resolver';
import { MainComponent } from './core/components/main/main.component';
import { NotificationResolver } from './core/resolvers/notification.resolver';
import { DashboardNgrxComponent } from './dashboard/components/dashboard-ngrx/dashboard-ngrx.component';
import { DashboardComponent } from './dashboard/components/dashboard/dashboard.component';
import { NotificationDetailsComponent } from './details/notification-details/notification-details.component';
import { OverviewComponent } from './details/overview/overview.component';
import { Forms2WrapperComponent } from './pace-forms-2-wrapper/components/forms2-wrapper/forms2-wrapper.component';
import { TimesheetAndExpensesComponent } from './timesheet/components/timesheet-and-expenses/timesheet-and-expenses.component';
import { TimesheetResolver } from './timesheet/components/timesheet-and-expenses/timesheet.resolver';
import { TimesheetCanDeactivateGuard } from './timesheet/timesheet.can-deactivate.guard';
import { deviceLockGuard } from './core/guards/device-lock.guard';
import { TimesheetCanActivateGuard } from './timesheet/timesheet.can-activate.guard';
import { SmartlockerComponent } from './smartlocker/components/smartlocker/smartlocker.component';
import { SmartlockerCanActivateGuard } from './smartlocker/smartlocker.can-activate.guard';
import { environment } from '../environments/environment';
import { ConfirmationIsLoadedGuard } from './confirmation/confirmation-is-loaded.guard';
import { ConfirmationTypeChangeGuard } from './confirmation/confirmation-type-change.guard';
import { TimesheetNgrxComponent } from './timesheet/components/timesheet-ngrx/timesheet-ngrx.component';
import { NotificationExistsGuard } from './core/guards/notification-exists.guard';
import { PaceSpectorNgrxComponent } from '../../../../libs/pace-core/src/lib/components/pace-spector/pace-spector-ngrx.component';

// Route-level swap for the timesheet NgRx migration. Off by default, so production keeps rendering
// TimesheetAndExpensesComponent.
const timesheetComponent = environment.enableNgRx ? TimesheetNgrxComponent : TimesheetAndExpensesComponent;

// Same route-level swap for the dashboard NgRx migration.
const dashboardComponent = environment.enableNgRx ? DashboardNgrxComponent : DashboardComponent;

const paceSpectorComponent = environment.enableNgRx ? PaceSpectorNgrxComponent : PaceSpectorComponent;

// The notification-details migration swaps the component and drops the resolver with it:
// NotificationDetailsComponent looks its notification up in the store from the route parameters, so
// there is nothing left for a resolver to pre-fetch.
const notificationDetailsRoute: Route = environment.enableNgRx
    ? { path: 'details/:frontid/:qmnum/:manum', component: NotificationDetailsComponent }
    : {
          path: 'details/:frontid/:qmnum/:manum',
          component: OverviewComponent,
          resolve: { notification: NotificationResolver },
      };
import { LocationTrackerComponent } from './location-tracker/components/location-tracker/location-tracker.component';

export const routes: Routes = [
    { path: '', redirectTo: 'main/dashboard', pathMatch: 'full' },
    {
        path: 'main',
        component: MainComponent,
        canActivate: [AppInitializationGuard],
        children: [
            { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
            {
                path: 'dashboard',
                component: dashboardComponent,
                canActivate: [deviceLockGuard],
            },
            {
                ...notificationDetailsRoute,
                runGuardsAndResolvers: 'always',
                canActivate: [deviceLockGuard, NotificationExistsGuard],
            },
            {
                path: 'confirmation/:frontid/:qmnum/:manum/:crmtype',
                component: ConfirmationComponent,
                canDeactivate: [ConfirmationCanDeactivateGuard],
                resolve: { confirmation: ConfirmationResolver, notification: NotificationResolver },
                canActivate: environment.enableNgRx
                    ? [
                          deviceLockGuard,
                          ConfirmationIsLoadedGuard,
                          NotificationExistsGuard,
                          ConfirmationCanActivateGuard,
                          // Last, so the technician is only asked once the route would otherwise open.
                          ConfirmationTypeChangeGuard,
                      ]
                    : [deviceLockGuard, ConfirmationIsLoadedGuard, NotificationExistsGuard],
            },
            {
                path: 'forms2/:frontid/:qmnum/:manum/:materialid/:equipmentid/:formid/:language/:lockmode',
                component: Forms2WrapperComponent,
                canActivate: [PaceForms2Guard],
                canDeactivate: [PaceForms2Guard],
            },
            {
                path: 'timesheet',
                component: timesheetComponent,
                canDeactivate: [TimesheetCanDeactivateGuard],
                canActivate: [deviceLockGuard, TimesheetCanActivateGuard, ConfirmationIsLoadedGuard],
            },
            {
                path: 'timesheet/:date',
                component: timesheetComponent,
                resolve: { date: TimesheetResolver },
                canDeactivate: [TimesheetCanDeactivateGuard],
                canActivate: [deviceLockGuard, TimesheetCanActivateGuard, ConfirmationIsLoadedGuard],
            },
            {
                path: 'widgets',
                component: WidgetsComponent,
            },
            {
                path: 'location-tracker',
                component: LocationTrackerComponent,
                canActivate: [deviceLockGuard],
            },
            {
                path: 'pace-spector',
                component: paceSpectorComponent,
                canActivate: [deviceLockGuard, ConfirmationIsLoadedGuard],
            },
            {
                path: 'docbox/:frontid/:qmnum/:manum',
                component: DocboxComponent,
            },
            {
                path: 'smartlocker',
                component: SmartlockerComponent,
                canActivate: [deviceLockGuard, SmartlockerCanActivateGuard],
            },
        ],
    },
    { path: '**', redirectTo: 'main/dashboard' },
];
